import { offerRepositoryApi } from '../api'
import type { OfferItem } from '../../hooks/shared/offers/offer-management/useOffersManagement'
import type { OnboardingStepKey } from '../../config/onboardingRegistry'
import { ALLEGRO_MAX_OFFER_IMAGES } from '../../hooks/marketplaces/allegro/shared/allegroLimits'
import { onboardingScoringComponentForStep } from './onboardingScoringPreference'
import { allegroOfferIdsFromOffers, isAllegroOfferId } from './tutorialOfferIdsFilter'

/** Ile ofert pokazujemy w filtrze ID podczas tutorialu. */
export const ONBOARDING_FILTER_COUNT = 15
/** Domyślnie ile ofert zaznaczamy checkboxami na start (min. 2 do ukończenia większości wizardów). */
export const ONBOARDING_PRESELECT_COUNT = 2
/** Grafikomat w przewodniku: 1 oferta (koszt ~8 pkt), preferowane oferty z małą liczbą zdjęć. */
export const ONBOARDING_GRAFIKOMAT_PRESELECT_COUNT = 1
/** Preferuj oferty poniżej tego limitu zdjęć w kroku Grafikomatu. */
export const ONBOARDING_GRAFIKOMAT_PREFERRED_MAX_IMAGES = 5
/** @deprecated Użyj ONBOARDING_FILTER_COUNT — zostawione dla kompatybilności bannera listy. */
export const ONBOARDING_PICK_COUNT = ONBOARDING_FILTER_COUNT
/** offer-repository odrzuca limit > 100 (422). */
const FETCH_LIMIT = 100

/** Limit zaznaczeń w hubie / wizardzie dla danego kroku przewodnika. */
export function onboardingPreselectCountForStep(stepKey?: OnboardingStepKey | null): number {
  if (stepKey === 'grafikomat_flow') return ONBOARDING_GRAFIKOMAT_PRESELECT_COUNT
  return ONBOARDING_PRESELECT_COUNT
}

function parseFirstInt(raw: string | undefined): number | null {
  if (!raw) return null
  const match = raw.match(/(\d+)/)
  return match ? Number.parseInt(match[1], 10) : null
}

function titleCharCount(offer: OfferItem): number {
  return (
    parseFirstInt(offer.scoring?.criterias?.title?.value) ?? offer.name.length
  )
}

function descriptionCharCount(offer: OfferItem): number {
  return parseFirstInt(offer.scoring?.criterias?.description?.value) ?? 0
}

function imageCount(offer: OfferItem): number {
  return parseFirstInt(offer.scoring?.criterias?.images?.value) ?? 0
}

function optionalParamsPercent(offer: OfferItem): number | null {
  const value = offer.scoring?.criterias?.parameters?.value
  if (!value) return null
  if (value.toLowerCase().includes('brak') || value.toLowerCase().includes('missing')) {
    return 0
  }
  const match = value.match(/(\d+)\s*%/)
  return match ? Number.parseInt(match[1], 10) : null
}

async function fetchOffers(
  accountId: number,
  params: Record<string, string | number | string[] | undefined>,
): Promise<OfferItem[]> {
  const qs = new URLSearchParams()
  qs.set('account_id', String(accountId))
  qs.set('page', '1')
  qs.set('limit', String(FETCH_LIMIT))
  qs.set('sort_by', 'scoring')
  qs.set('sort_order', 'asc')
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue
    if (Array.isArray(value)) {
      value.forEach((item) => qs.append(key, String(item)))
    } else {
      qs.set(key, String(value))
    }
  }
  try {
    const { data } = await offerRepositoryApi.get<{ offers: OfferItem[] }>(
      `/allegro/offers-management?${qs}`,
    )
    return data.offers ?? []
  } catch {
    return []
  }
}

/**
 * Rank all Allegro-compatible offers and take the top N.
 * No quality predicates — filtering by title/desc/params thresholds was too
 * aggressive for onboarding (empty/tiny pools). Ranking still surfaces the
 * best-improvement candidates first so preselect (max 2) stays useful.
 */
function pickRanked(
  offers: OfferItem[],
  rank: (offer: OfferItem) => number,
): OfferItem[] {
  const validOffers = offers.filter((offer) => isAllegroOfferId(offer.id))
  const pool = validOffers.length > 0 ? validOffers : offers
  return [...pool].sort((a, b) => rank(a) - rank(b)).slice(0, ONBOARDING_FILTER_COUNT)
}

function idsFrom(offers: OfferItem[]): string[] {
  return allegroOfferIdsFromOffers(offers).slice(0, ONBOARDING_FILTER_COUNT)
}

/** Dopełnij do ONBOARDING_FILTER_COUNT dowolnymi ofertami z konta (zachowaj kolejność priority). */
async function padOfferIds(accountId: number, priorityIds: string[]): Promise<string[]> {
  const unique = [...new Set(filterAllegroCompatible(priorityIds))]
  if (unique.length >= ONBOARDING_FILTER_COUNT) {
    return unique.slice(0, ONBOARDING_FILTER_COUNT)
  }
  const filler = await pickGenericOffers(accountId)
  const seen = new Set(unique)
  for (const id of filler) {
    if (seen.has(id)) continue
    unique.push(id)
    seen.add(id)
    if (unique.length >= ONBOARDING_FILTER_COUNT) break
  }
  return unique.slice(0, ONBOARDING_FILTER_COUNT)
}

function filterAllegroCompatible(ids: string[]): string[] {
  return ids.filter((id) => isAllegroOfferId(id))
}

/** Tytuły — ranking: krótki tytuł + bogaty opis na górze (bez odcinania ofert). */
export async function pickTitleFlowOffers(accountId: number): Promise<string[]> {
  const offers = await fetchOffers(accountId, {
    scoring_components: onboardingScoringComponentForStep('titles_flow') ?? 'title',
  })
  const pool = offers.length > 0 ? offers : await fetchOffers(accountId, {})

  const picked = pickRanked(pool, (o) => {
    const titleGap = Math.max(0, titleCharCount(o) - 59)
    const descGap = Math.max(0, 501 - descriptionCharCount(o))
    return titleGap * 10 + descGap
  })

  return padOfferIds(accountId, idsFrom(picked))
}

/** Opisy — ranking: najkrótsze opisy pierwsze (bez odcinania ofert). */
export async function pickDescriptionFlowOffers(accountId: number): Promise<string[]> {
  const offers = await fetchOffers(accountId, {
    scoring_components: onboardingScoringComponentForStep('descriptions_flow') ?? 'description',
  })
  const pool = offers.length > 0 ? offers : await fetchOffers(accountId, {})

  const picked = pickRanked(pool, (o) => descriptionCharCount(o) || 9999)

  return padOfferIds(accountId, idsFrom(picked))
}

/** Parametry — ranking: niski % opcjonalnych + długi opis na górze (bez odcinania). */
export async function pickParameterFlowOffers(accountId: number): Promise<string[]> {
  const offers = await fetchOffers(accountId, {
    scoring_components: onboardingScoringComponentForStep('parameters_flow') ?? 'parameters',
  })
  const pool = offers.length > 0 ? offers : await fetchOffers(accountId, {})

  const picked = pickRanked(pool, (o) => {
    const optional = optionalParamsPercent(o) ?? 100
    const descGap = Math.max(0, 501 - descriptionCharCount(o))
    return optional * 10 + descGap
  })

  return padOfferIds(accountId, idsFrom(picked))
}

/** Kopiowanie / zestawy — dowolne aktywne oferty. */
export async function pickGenericOffers(accountId: number): Promise<string[]> {
  const offers = await fetchOffers(accountId, {})
  return idsFrom(offers.slice(0, ONBOARDING_FILTER_COUNT))
}

/**
 * Grafikomat — preferuj oferty z <5 zdjęć; wyklucz pełne galerie (16 = limit Allegro).
 * Preselect w hubie bierze 1 ofertę (koszt generacji).
 */
export async function pickGrafikomatFlowOffers(accountId: number): Promise<string[]> {
  const offers = await fetchOffers(accountId, {
    scoring_components: onboardingScoringComponentForStep('grafikomat_flow') ?? 'images',
    sort_order: 'asc',
  })
  const pool = offers.length > 0 ? offers : await fetchOffers(accountId, { sort_order: 'asc' })
  const valid = pool.filter((offer) => isAllegroOfferId(offer.id))
  const base = valid.length > 0 ? valid : pool

  const underPreferred = base.filter((o) => {
    const n = imageCount(o)
    return n > 0 && n < ONBOARDING_GRAFIKOMAT_PREFERRED_MAX_IMAGES
  })
  const underMax = base.filter((o) => {
    const n = imageCount(o)
    return n > 0 && n < ALLEGRO_MAX_OFFER_IMAGES
  })
  const rankedPool =
    underPreferred.length > 0 ? underPreferred : underMax.length > 0 ? underMax : base

  const ranked = [...rankedPool].sort((a, b) => {
    const ai = imageCount(a)
    const bi = imageCount(b)
    // Prefer fewer images; unknown/0 last.
    const aKey = ai > 0 ? ai : 999
    const bKey = bi > 0 ? bi : 999
    return aKey - bKey
  })

  return padOfferIds(accountId, idsFrom(ranked))
}

export async function pickOffersForOnboardingStep(
  accountId: number,
  stepKey: OnboardingStepKey,
): Promise<string[]> {
  switch (stepKey) {
    case 'titles_flow':
      return pickTitleFlowOffers(accountId)
    case 'descriptions_flow':
      return pickDescriptionFlowOffers(accountId)
    case 'grafikomat_flow':
      return pickGrafikomatFlowOffers(accountId)
    case 'parameters_flow':
      return pickParameterFlowOffers(accountId)
    case 'copy_offer':
    case 'create_bundle':
      return pickGenericOffers(accountId)
    default:
      return []
  }
}
